import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// API paths
const API_PATHS = {
  REPORTS: '/calls/reports',
};

// Thunk for fetching a specific report ID by type
export const fetchReportIdByType = createAsyncThunk('reports/fetchReportIdByType', async ({ reportType, axiosInstance }, { rejectWithValue }) => {
  try {
    if (!axiosInstance) {
      throw new Error('Axios instance is required');
    }

    const response = await axiosInstance.get(API_PATHS.REPORTS, {
      params: {
        limit: 1,
        offset: 0,
        reportType,
      },
    });

    const reportData = response?.data?.data[0];
    return {
      type: reportType,
      id: reportData?.id || null,
      createdAt: reportData?.createdAt || null,
    };
  } catch (error) {
    return rejectWithValue({
      type: reportType,
      error: error.response?.data?.message || `Failed to fetch ${reportType} report ID`,
    });
  }
});

// Thunk for fetching all report IDs at once - API is single source of truth for IDs and dates
export const fetchAllReportIds = createAsyncThunk('reports/fetchAllReportIds', async (axiosInstance, { rejectWithValue }) => {
  try {
    if (!axiosInstance) {
      throw new Error('Axios instance is required');
    }

    const reportTypes = ['INTAKE', 'INTAKE1', 'INTAKE2', 'INTAKE3'];

    // Make all API calls in parallel with Promise.all for efficiency
    const requests = reportTypes.map(async (reportType) => {
      try {
        const response = await axiosInstance.get(API_PATHS.REPORTS, {
          params: {
            limit: 1,
            offset: 0,
            reportType,
          },
        });

        const reportData = response?.data?.data[0];
        const createdAt = reportData?.createdAt || null;

        return {
          type: reportType,
          id: reportData?.id || null,
          createdAt,
          success: true,
          // Distinguish between no data returned vs actual error
          notFound: !reportData?.id,
        };
      } catch (error) {
        return {
          type: reportType,
          id: null,
          createdAt: null,
          success: false,
          notFound: false,
          error: error.response?.data?.message || `Failed to fetch ${reportType} report ID`,
        };
      }
    });

    const results = await Promise.all(requests);

    // Process results
    const reportIds = {};
    const reportDates = {};
    const errors = [];

    results.forEach((result) => {
      // Always update from API data - API is source of truth
      if (result.success) {
        reportIds[result.type] = result.id;
        reportDates[result.type] = result.createdAt;
      }

      if (!result.success) {
        errors.push(result.error);
      }
    });

    return { reportIds, reportDates, errors };
  } catch (error) {
    return rejectWithValue('Failed to fetch report IDs');
  }
});

// Initial state
const initialState = {
  reportIds: {
    INTAKE: null,
    INTAKE1: null,
    INTAKE2: null,
    INTAKE3: null,
  },
  reportDates: {
    INTAKE: null,
    INTAKE1: null,
    INTAKE2: null,
    INTAKE3: null,
  },
  loading: false,
  error: null,
  hasFetched: false,
};

// Create the slice
const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    // Synchronous action to set a specific report ID (useful for user metadata sync)
    setReportId: (state, action) => {
      const { type, id, createdAt } = action.payload;
      state.reportIds[type] = id;
      if (createdAt !== undefined) {
        state.reportDates[type] = createdAt;
      }
    },

    // Clear all report IDs (useful for logout)
    clearReportIds: (state) => {
      state.reportIds = initialState.reportIds;
      state.reportDates = initialState.reportDates;
      state.hasFetched = false;
      state.error = null;
    },

    // Reset error state
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchReportIdByType
    builder
      .addCase(fetchReportIdByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportIdByType.fulfilled, (state, action) => {
        const { type, id, createdAt } = action.payload;
        state.reportIds[type] = id;
        state.reportDates[type] = createdAt;
        state.loading = false;
      })
      .addCase(fetchReportIdByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error;
      });

    // Handle fetchAllReportIds
    builder
      .addCase(fetchAllReportIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllReportIds.fulfilled, (state, action) => {
        const { reportIds, reportDates, errors } = action.payload;

        // Always update from API data - API is single source of truth for both IDs and dates
        Object.keys(state.reportIds).forEach((type) => {
          // Update ID from API response
          if (Object.prototype.hasOwnProperty.call(reportIds, type)) {
            state.reportIds[type] = reportIds[type];
          }

          // Update date from API response
          if (Object.prototype.hasOwnProperty.call(reportDates, type)) {
            state.reportDates[type] = reportDates[type];
          }
        });

        state.loading = false;
        state.hasFetched = true;

        // Only set error if all requests failed
        if (errors.length > 0 && Object.values(reportIds).every((id) => !id)) {
          state.error = 'Failed to fetch any report IDs';
        }
      })
      .addCase(fetchAllReportIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasFetched = true;
      });
  },
});

// Export actions
export const { setReportId, clearReportIds, clearError } = reportSlice.actions;

// Export selectors
export const selectAllReportIds = (state) => state.reports.reportIds;
export const selectAllReportDates = (state) => state.reports.reportDates;
export const selectReportIdByType = (type) => (state) => state.reports.reportIds[type];
export const selectReportDateByType = (type) => (state) => state.reports.reportDates[type];
export const selectReportsLoading = (state) => state.reports.loading;
export const selectReportsError = (state) => state.reports.error;
export const selectReportsHasFetched = (state) => state.reports.hasFetched;

// Export reducer
export default reportSlice.reducer;
